import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import api from '../services/api';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const emptyUser = {
  name: '',
  email: '',
  password: '',
  role: 'team_leader',
  team_size: 1,
  preferred_days: [],
  color: '#4caf50',
  abbreviation: '',
  can_view_report: false,
};

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({ ...emptyUser });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenAdd = () => {
    setEditUser(null);
    setFormData({ ...emptyUser });
    setDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setEditUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'team_leader',
      team_size: user.team_size || user.teamSize || 1,
      preferred_days: user.preferred_days || user.preferredDays || [],
      color: user.color || '#4caf50',
      abbreviation: user.abbreviation || '',
      can_view_report: !!user.can_view_report,
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditUser(null);
    setFormData({ ...emptyUser });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => {
      const days = prev.preferred_days.includes(day)
        ? prev.preferred_days.filter((d) => d !== day)
        : [...prev.preferred_days, day];
      return { ...prev, preferred_days: days };
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = { ...formData };
      if (editUser && !payload.password) {
        delete payload.password;
      }

      if (editUser) {
        await api.put(`/users/${editUser._id || editUser.id}`, payload);
        setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
      } else {
        await api.post('/users', payload);
        setSnackbar({ open: true, message: 'User created successfully!', severity: 'success' });
      }
      handleClose();
      fetchUsers();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Operation failed.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const { user } = deleteDialog;
    if (!user) return;

    try {
      await api.delete(`/users/${user._id || user.id}`);
      setSnackbar({ open: true, message: 'User deleted successfully!', severity: 'success' });
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete user.',
        severity: 'error',
      });
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.abbreviation?.toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Manage Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenAdd}
          sx={{ backgroundColor: '#052e2b' }}
        >
          Add User
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by name, email or abbreviation…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 340 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">
              {search ? 'No users match your search.' : 'No users found.'}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Team Size</strong></TableCell>
                <TableCell><strong>Abbreviation</strong></TableCell>
                <TableCell><strong>Color</strong></TableCell>
                <TableCell><strong>Preferred Days</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((u) => (
                <TableRow key={u._id || u.id} hover>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.role === 'admin' ? 'Admin' : 'Team Leader'}
                      size="small"
                      color={u.role === 'admin' ? 'error' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>{u.team_size || u.teamSize || '-'}</TableCell>
                  <TableCell>{u.abbreviation || '-'}</TableCell>
                  <TableCell>
                    {u.color ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: u.color,
                            border: '1px solid #ccc',
                          }}
                        />
                        <Typography variant="caption">{u.color}</Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(u.preferred_days || u.preferredDays || []).map((day) => (
                        <Chip
                          key={day}
                          label={day.substring(0, 3)}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                      {!(u.preferred_days || u.preferredDays || []).length && (
                        <Typography variant="caption" color="text.secondary">None</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(u)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, user: u })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={editUser ? 'Password (leave blank to keep current)' : 'Password'}
                  type="password"
                  fullWidth
                  required={!editUser}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    label="Role"
                    onChange={(e) => handleChange('role', e.target.value)}
                  >
                    <MenuItem value="team_leader">Team Leader</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Team Size"
                  type="number"
                  fullWidth
                  value={formData.team_size}
                  onChange={(e) => handleChange('team_size', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 50 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Abbreviation"
                  fullWidth
                  value={formData.abbreviation}
                  onChange={(e) => handleChange('abbreviation', e.target.value)}
                  placeholder="e.g. AK, SM, JP"
                  helperText="Short label shown on booked seats"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Leader Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      backgroundColor: formData.color || '#cccccc',
                      border: '2px solid #bbb',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                    onClick={() => document.getElementById('color-picker-input').click()}
                  />
                  <input
                    id="color-picker-input"
                    type="color"
                    value={formData.color || '#cccccc'}
                    onChange={(e) => handleChange('color', e.target.value)}
                    style={{ width: 0, height: 0, padding: 0, border: 'none', visibility: 'hidden', position: 'absolute' }}
                  />
                  <TextField
                    size="small"
                    value={formData.color || ''}
                    onChange={(e) => handleChange('color', e.target.value)}
                    placeholder="#E53935"
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 7 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Preferred Days
                </Typography>
                <FormGroup row>
                  {DAYS_OF_WEEK.map((day) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={formData.preferred_days.includes(day)}
                          onChange={() => handleDayToggle(day)}
                          size="small"
                        />
                      }
                      label={day}
                    />
                  ))}
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Permissions
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.can_view_report}
                      onChange={(e) => handleChange('can_view_report', e.target.checked)}
                      size="small"
                    />
                  }
                  label="View Booking Report"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.name || !formData.email}
            sx={{ backgroundColor: '#052e2b' }}
          >
            {saving ? <CircularProgress size={20} /> : editUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog.user?.name}</strong>? This will also remove all their bookings. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageUsers;
